// Review phase — "Xem lại 3 biểu cảm khuôn mặt" (new flow, custom path).
// docs/fe-state-machine.md.
//
// Shows the player's composed faces. Tap a thumbnail to preview that emotion
// large (the frame's "Chọn biểu cảm để xem trước nhân vật"). "Chụp lại hình"
// retakes the previewed emotion (→ back to Capture for it). "Tiếp tục" bakes the
// character and proceeds to the guide → game.
//
// Reached after Capture's upload succeeds: ctx.assets.character holds all three
// composed-head URLs.

import type { Emotion, Phase } from "../state/types";
import { mountFrame, clearChildren } from "../ui/overlay";
import { apiBase } from "../api/client";
import { buildCharacterSprites } from "../game/buildCharacter";

const FLOW = "/textures/flow";
const EMOTIONS: Emotion[] = ["neutral", "happy", "sad"];

// Regions of frameCheckCharacter (%).
// Centered on the frame's rounded preview box (measured center ≈ 38.6% / 49.9%).
const PREVIEW_BOX = "top:11.5%;left:8%;width:84%;height:54%;";
const THUMBS_ROW = "top:71.5%;left:10%;width:80%;height:14%;";

let cleanup: (() => void) | null = null;

export const Review: Phase = {
  enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);

    const bust = Date.now();
    const headUrl = (e: Emotion): string | null => {
      const u = ctx.assets.character?.[e];
      if (!u) return null;
      // S3 trả về absolute URL, filesystem trả về relative path
      const base = u.startsWith("http") ? "" : apiBase();
      return `${base}${u}?r=${bust}`;
    };

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.cssText =
      "position:relative;display:flex;flex-direction:column;align-items:center;padding:2% 4%;box-sizing:border-box;gap:1.5%;";

    frame.innerHTML = `
      <div style="position:relative;width:100%;aspect-ratio:1500/2332;flex:0 0 auto;
        background:url(${FLOW}/frameCheckCharacter.png) center/100% 100% no-repeat;">
        <div id="preview" style="position:absolute;${PREVIEW_BOX}background:center/contain no-repeat;"></div>
        <div id="thumbs" style="position:absolute;${THUMBS_ROW}display:flex;gap:5%;align-items:center;justify-content:center;"></div>
      </div>
      <div style="display:flex;gap:4%;width:100%;justify-content:center;margin-top:auto;">
        <button id="retake" style="width:42%;background:none;border:0;padding:0;cursor:pointer;
          background-image:url(${FLOW}/buttonRecapture.png);background-size:contain;background-repeat:no-repeat;
          background-position:center;aspect-ratio:636/216;" aria-label="Chụp lại hình"></button>
        <button id="continue" style="width:42%;background:none;border:0;padding:0;cursor:pointer;
          background-image:url(${FLOW}/buttonContinue.png);background-size:contain;background-repeat:no-repeat;
          background-position:center;aspect-ratio:612/208;" aria-label="Tiếp tục"></button>
      </div>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const previewEl = frame.querySelector<HTMLElement>("#preview")!;
    const thumbsEl = frame.querySelector<HTMLElement>("#thumbs")!;
    const retakeBtn = frame.querySelector<HTMLButtonElement>("#retake")!;
    const continueBtn = frame.querySelector<HTMLButtonElement>("#continue")!;

    let selected: Emotion = "neutral";

    // Thumbnails (tap to preview).
    const thumbEls: Partial<Record<Emotion, HTMLButtonElement>> = {};
    EMOTIONS.forEach((e) => {
      const t = document.createElement("button");
      t.dataset.emotion = e;
      const url = headUrl(e);
      t.style.cssText =
        `width:28%;aspect-ratio:1;background:${url ? `url(${url}) center/cover no-repeat` : "#f0d0dc"};border:3px solid transparent;border-radius:12px;cursor:pointer;padding:0;`;
      t.addEventListener("click", () => select(e));
      thumbsEl.appendChild(t);
      thumbEls[e] = t;
    });

    const select = (e: Emotion) => {
      selected = e;
      const url = headUrl(e);
      previewEl.style.backgroundImage = url ? `url(${url})` : "none";
      EMOTIONS.forEach((x) => {
        thumbEls[x]!.style.borderColor = x === e ? "#e23b6d" : "transparent";
      });
    };
    select("neutral");

    // Retake the previewed emotion → drop it, go back to Capture.
    retakeBtn.addEventListener("click", () => {
      if (ctx.assets.character) delete ctx.assets.character[selected];
      delete ctx.capture[selected];
      transition("Capture");
    });

    // Proceed → bake the character, then the guide → game.
    continueBtn.addEventListener("click", async () => {
      continueBtn.style.pointerEvents = "none";
      continueBtn.style.opacity = "0.6";
      const shared = ctx.assets.shared;
      if (!shared) {
        console.error("[Review] missing shared assets");
        return;
      }
      try {
        const characterSprites = await buildCharacterSprites(ctx, shared);
        transition("Instructions", { assets: { ...ctx.assets, characterSprites } });
      } catch (err) {
        console.error("[Review] bake failed", err);
        continueBtn.style.pointerEvents = "auto";
        continueBtn.style.opacity = "1";
      }
    });
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
